import HomeSectionFooter from "@pages/Home/components/HomeSectionFooter";
import AccessLink from "@components/AccessLink/AccessLink";
import { toast } from "react-toastify";
import { requestTrialByFeature } from "@actions/page";
import { useDispatch } from "react-redux";
import { AppId, FCX, usePlatformContext } from "@datassential/platform-ui-lib";
import React, { useCallback, useEffect, useMemo } from "react";
import { useHomeGetTopGrowingKeywords } from "@apiHooks/useHomeGetTopGrowingKeywords";
import HomeSectionLoader from "@pages/Home/components/HomeSectionLoader";
import { PermissionLevelEnum } from "@models/PermissionSection";
import './HomeFoodsMenuGrowth.styles.scss';
import { CSSTransition } from "react-transition-group";
import HomeFoodsMenuGrowthSlide from "@pages/Home/sections/HomeFoods/HomeFoodsMenuGrowthSlide";

const HomeFoodsMenuGrowth: FCX = () => {
    const dispatch = useDispatch();
    const { userInfo: { products } } = usePlatformContext();
    const { isLoading, data: responseData } = useHomeGetTopGrowingKeywords();
    const [activeCategoryId, setActiveCategoryId] = React.useState<number | null>(null);

    const {
        permissionLevel,
        data: rawData,
    } = responseData || {};

    const data = useMemo(
        () => rawData || [],
        [rawData]
    );

    const hasAccess = useMemo(
        () => permissionLevel === PermissionLevelEnum.Full,
        [permissionLevel]
    );

    const categories = useMemo(
        () => data.map(i => ({
            id: i.topTrendCategoryId,
            name: i.topTrendCategoryName,
        })),
        [data]
    );

    useEffect(() => {
        setActiveCategoryId(categories?.[0]?.id ?? null);
    }, [categories]);

    const requestTrial = () => {
        toast.info('Request has been sent');
        return dispatch(requestTrialByFeature('mtd'));
    };

    const menuTrends = useMemo(
        () => products[AppId.MenuTrends],
        [products]
    );

    const onPrev = useCallback(
        () => {
            const activeIndex = categories.findIndex(i => i.id === activeCategoryId);
            const prevIndex = (activeIndex - 1 + categories.length) % categories.length;
            setActiveCategoryId(categories[prevIndex]?.id ?? null);
        },
        [activeCategoryId, categories]
    );

    const onNext = useCallback(
        () => {
            const activeIndex = categories.findIndex(i => i.id === activeCategoryId);
            const nextIndex = (activeIndex + 1) % categories.length;
            setActiveCategoryId(categories[nextIndex]?.id ?? null);
        },
        [activeCategoryId, categories]
    );

    if (isLoading) {
        return (
            <HomeSectionLoader/>
        );
    }

    return (
        <div className="HomeSection__content">
            <div className="HomeFoodsMenuGrowth">
                {data.map(i => (
                    <CSSTransition
                        key={i.topTrendCategoryId}
                        in={i.topTrendCategoryId === activeCategoryId}
                        timeout={1000}
                        unmountOnExit={true}
                    >
                        <HomeFoodsMenuGrowthSlide
                            data={i}
                            onPrev={onPrev}
                            onNext={onNext}
                        />
                    </CSSTransition>

                ))}
            </div>
            <HomeSectionFooter>
                <AccessLink
                    url={menuTrends.url ?? ''}
                    text="More Menu Trends"
                    altText="Upgrade your subscription to unlock menu insights."
                    hasAccess={hasAccess}
                    onContactUsClick={requestTrial}
                    testId="linkHomeFoodsMenuGrowthMoreLaunches"
                />
            </HomeSectionFooter>
        </div>
    );
};

export default HomeFoodsMenuGrowth;